/**
 * GLDN Cart Builder Actions — Edit / Duplicate / Remove + Preview Piece
 * for personalized (builder) line items in the Horizon cart drawer.
 *
 * Edit: native <a> href = product page with ?p={lineKey} — personalizer's
 *   populate() picks up that param and pre-fills from the cart item.
 *
 * Duplicate: opens a "BEFORE YOU CONTINUE" modal (only for Builder items)
 *   matching the legacy theme behavior. Two paths:
 *     - "ADD WITHOUT EDITING" → immediate duplicate (no UI dance)
 *     - "EDIT BEFORE ADDING"  → duplicate, then navigate to the PDP with
 *       ?p={new_main_key} so the personalizer pre-fills and the user can
 *       tweak the duplicated piece. Save on the personalizer remove+re-
 *       adds the line under a fresh Builder (handled by the existing
 *       updateBuilderKey path in gldn-pdp-personalization-js.liquid).
 *
 *   Either path clones the personalizer "set" — main item + every add-on
 *   line sharing the source's Builder ID — under a fresh Builder. Gift /
 *   Gift Note are intentionally stripped per the brief's "second
 *   recipient" framing (APEX 42a2ded5).
 *
 * Remove: POSTs to /cart/change.js with quantity 0 for the line item.
 *
 * Preview Piece: opens a lightweight modal showing the uploaded preview
 *   image for the line.
 */
(function () {
  function randomBuilder() {
    return Math.random().toString(36).slice(2, 8);
  }

  function randomUnique() {
    return Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);
  }

  /** The section ids every rendered <cart-items-component> is bound to — the
   *  drawer's (which is the HEADER section, since the drawer markup lives in
   *  snippets/header-actions.liquid) and the /cart page's. Mirrors what
   *  component-cart-items.js's updateQuantity() collects. */
  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      var id = el.dataset && el.dataset.sectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  /** Announce a cart mutation, carrying rendered section HTML when Shopify
   *  returned it.
   *
   *  This matters more than it looks. component-cart-items.js's cart:update
   *  handler morphs from `detail.data.sections[sectionId]` when it's present,
   *  and otherwise falls back to `sectionRenderer.renderSection(sectionId,
   *  {cache:false})` — a second, blind round-trip that re-fetches and FULL-morphs
   *  the entire header section. Duplicate/Remove were the only cart mutations in
   *  the theme that took that fallback, and it is fragile in two ways the
   *  quantity/remove flows are not:
   *
   *    1. If that re-render returns anything the morph can't use, morphSection
   *       throws 'Section <id> not found in the section rendering response'.
   *       Nothing catches it, so the cart is left showing whatever partial state
   *       it had and never recovers without a page reload — the reported
   *       "stuck header-only cart" (gldn99 #416).
   *    2. A full morph of the header section is also what destroys the gldn
   *       header injection (#386) and re-renders an open top-layer <dialog>.
   *
   *  Asking /cart/add.js and /cart/change.js for `sections` costs nothing (it
   *  rides the mutation we already issue) and puts Duplicate on the exact same
   *  render path as the quantity selector. */
  function dispatchCartUpdate(cartResponse) {
    var sections = (cartResponse && cartResponse.sections) || null;
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: null,
        sourceId: 'cart-builder-actions',
        data: { source: 'cart-builder-actions', sections: sections }
      }
    }));
  }

  /** Duplicate a cart line and (optionally) every add-on line sharing its
   *  Builder ID. Returns the new main item (the cloned source) from the
   *  /cart/add.js response so the caller can navigate to it for the
   *  "Edit Before Adding" flow.
   *
   *  Returns: { mainItem, allItems } | null on failure. */
  async function duplicateItem(key, oldBuilder) {
    // Snapshot the full cart so we can find every add-on line that belongs
    // to the source set (same Builder ID). The personalizer represents a
    // personalized piece as a multi-line set: 1 main line + N add-on lines,
    // all sharing one Builder.
    const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!cartRes.ok) return null;
    const cart = await cartRes.json();
    const items = cart.items || [];

    // Try the exact-key lookup first. The key flows from row.data-key,
    // which gldn-gift-note.js keeps in sync when its own UI rotates the
    // key. But any cart re-render (Horizon morph, mid-flight
    // /cart/change.js from a different source, multi-line
    // gift-note.findNewKey ambiguity after a prior duplicate) can leave
    // row.data-key pointing at a key that no longer exists in the cart.
    // Falling back to Builder-ID matching resolves this — the Builder ID
    // is unique per personalizer set and is preserved across property
    // rotations.
    let sourceItem = items.find(function (i) { return i.key === key; });
    if (!sourceItem && oldBuilder) {
      sourceItem = items.find(function (i) {
        return i.properties
            && i.properties.Builder === oldBuilder
            && !i.properties._tag;
      });
    }
    if (!sourceItem) return null;

    const sourceBuilder = (sourceItem.properties && sourceItem.properties.Builder) || oldBuilder || null;
    const addonItems = sourceBuilder
      ? items.filter(function (i) {
          return i.key !== sourceItem.key
              && i.properties
              && i.properties.Builder === sourceBuilder;
        })
      : [];

    const newBuilder = randomBuilder();

    // Build a fresh line spec for one source item. Builder is rewritten
    // to the new set's id; _unique gets a fresh value so Shopify doesn't
    // merge the new line with the source line on identical properties.
    //
    // Gift / Gift Note are intentionally NOT copied. APEX 42a2ded5 frames
    // the duplicate flow as "the same personalized piece for a second
    // recipient" — pre-filling the original's gift recipient onto the new
    // line works against that intent.
    function buildLine(src) {
      const props = {};
      Object.keys(src.properties || {}).forEach(function (k) {
        if (k === 'Builder' || k === '_unique') return;
        if (k === 'Gift' || k === 'Gift Note') return;
        const v = src.properties[k];
        if (v != null) props[k] = v;
      });
      // Personalized sets are re-tagged under the new Builder id so the
      // cloned set stays grouped and distinct from the source. A
      // non-personalized line has no Builder — `_unique` alone forces a
      // separate cart line, and stamping a Builder prop would wrongly make
      // a plain product render as a personalizer set.
      if (sourceBuilder) props.Builder = newBuilder;
      props._unique = randomUnique();
      // "Buy another for a different recipient" on a plain item starts at a
      // single unit (the new recipient gets one; qty is adjustable after).
      // Builder sets preserve their per-line quantities (add-on ratios).
      const quantity = sourceBuilder ? (src.quantity || 1) : 1;
      return { id: src.variant_id, quantity: quantity, properties: props };
    }

    // Shopify's batched /cart/add.js inserts each item at the TOP of the
    // cart, sequentially — so the LAST item in the array ends up on top.
    // To produce [main, addon1, addon2, ...] visible order at the top of
    // the cart, reverse the array before posting so main is the final
    // (and therefore top-most) insertion.
    const itemsToAdd = [sourceItem].concat(addonItems).map(buildLine).reverse();

    // Request the cart sections alongside the add so the re-render below has
    // real HTML to morph rather than forcing a blind second fetch (see
    // dispatchCartUpdate).
    const sectionIds = cartSectionIds();
    const addBody = { items: itemsToAdd };
    if (sectionIds.length) {
      addBody.sections = sectionIds.join(',');
      addBody.sections_url = window.location.pathname;
    }

    const addRes = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(addBody)
    });
    if (!addRes.ok) return null;
    const addResp = await addRes.json();

    // Find the new main line in the response — the one without _tag.
    // Shopify echoes items in the order they were added (which we reversed),
    // so iterate to find the main rather than assuming a position.
    const respItems = (addResp && addResp.items) || [];
    const mainItem = respItems.find(function (i) {
      return i && i.properties && !i.properties._tag;
    }) || respItems[0] || null;

    dispatchCartUpdate(addResp);
    return { mainItem: mainItem, allItems: respItems };
  }

  // ── BEFORE YOU CONTINUE modal ─────────────────────────────────────────
  //
  // Matches the legacy theme: a small modal asks the customer whether to
  // edit the personalization before adding the duplicate to the cart, with
  // two buttons. Built as a native <dialog> for free focus management /
  // Esc-to-close. CSS lives inline so the snippet is self-contained.

  function ensureDuplicateModal() {
    let dialog = document.getElementById('gldn-duplicate-modal');
    if (dialog) return dialog;

    dialog = document.createElement('dialog');
    dialog.id = 'gldn-duplicate-modal';
    dialog.className = 'gldn-duplicate-modal';
    dialog.setAttribute('aria-labelledby', 'gldn-duplicate-modal__title');
    dialog.innerHTML =
      '<form method="dialog" class="gldn-duplicate-modal__form">' +
        '<button type="submit" value="cancel" class="gldn-duplicate-modal__close" aria-label="Close">&times;</button>' +
        '<span class="gldn-duplicate-modal__eyebrow">Before You Continue</span>' +
        '<p id="gldn-duplicate-modal__title" class="gldn-duplicate-modal__title">' +
          'Do you want to make changes to the piece you duplicated before adding it to your cart?' +
        '</p>' +
        '<div class="gldn-duplicate-modal__buttons">' +
          '<button type="submit" value="add" class="gldn-duplicate-modal__btn gldn-duplicate-modal__btn--add">' +
            'Add Without Editing' +
          '</button>' +
          '<button type="submit" value="edit" class="gldn-duplicate-modal__btn gldn-duplicate-modal__btn--edit">' +
            'Edit Before Adding' +
          '</button>' +
        '</div>' +
      '</form>';

    document.body.appendChild(dialog);
    return dialog;
  }

  /** Show the modal and resolve to "add" | "edit" | "cancel" based on
   *  which button the user pressed. */
  function openDuplicateModal() {
    const dialog = ensureDuplicateModal();

    // Defensive: if the dialog is already-open from some prior interaction
    // (close event missed, page state leaked across navigations), close
    // it first. showModal() throws InvalidStateError on an already-open
    // dialog — which would silently fail the open and leave the user
    // with no modal, perceived as "Duplicate ran without asking me."
    if (dialog.hasAttribute('open')) {
      try { dialog.close('cancel'); } catch (e) {}
    }
    // Reset returnValue so a leaked prior choice can't resolve this Promise.
    dialog.returnValue = '';

    return new Promise(function (resolve) {
      // The <form method="dialog"> submits with the clicked button's value,
      // which we read off dialog.returnValue after close.
      function onClose() {
        dialog.removeEventListener('close', onClose);
        resolve(dialog.returnValue || 'cancel');
      }
      dialog.addEventListener('close', onClose);
      // showModal() centers + dims background + traps focus.
      if (typeof dialog.showModal === 'function') {
        try {
          dialog.showModal();
        } catch (e) {
          // showModal can throw on unexpected dialog state. Tear down
          // the listener we just registered and degrade to the "add"
          // path so the user still gets the duplicate they asked for.
          dialog.removeEventListener('close', onClose);
          resolve('add');
        }
      } else {
        // Older browser fallback: open as non-modal. Esc still closes via the form.
        dialog.setAttribute('open', '');
        resolve('add'); // no real modal interaction available; degrade to "add"
      }
    });
  }

  async function handleDuplicateClick(itemKey, oldBuilder) {
    // Only items with a Builder get the modal. Non-builder items (e.g.
    // unpersonalized adds shouldn't reach the Duplicate button per
    // cart-products.liquid's render gate, but stay defensive) duplicate
    // immediately to match legacy behavior.
    if (!oldBuilder) {
      await duplicateItem(itemKey, oldBuilder);
      return;
    }

    const choice = await openDuplicateModal();
    if (choice === 'cancel') return;

    const result = await duplicateItem(itemKey, oldBuilder);
    if (!result || !result.mainItem) return;

    if (choice === 'edit') {
      // Navigate to the PDP for the NEW main line. Personalizer's
      // populate() reads ?p=<key>, sets updateBuilderKey, pre-fills, and
      // flips the ATC label to "Update Personalization". Saving there
      // removes the duplicate's lines (matched by Builder) and re-adds
      // under a fresh Builder — effectively an in-place edit of the
      // duplicate.
      const main = result.mainItem;
      const sep = main.url.indexOf('?') >= 0 ? '&' : '?';
      location.href = main.url + sep + 'p=' + encodeURIComponent(main.key);
    }
  }

  // ── Preview modal ────────────────────────────────────────────────────
  //
  // Native <dialog> + showModal() so the preview renders in the browser
  // top layer — ABOVE the cart drawer, which is itself a showModal()
  // dialog (.cart-drawer__dialog:modal). The prior body-level <div> could
  // not clear the drawer's top-layer stacking context with z-index alone,
  // so it rendered behind the drawer on desktop and was fully occluded by
  // the full-screen drawer on mobile, where the click appeared to do
  // nothing (#267). Mirrors the duplicate modal above.

  function ensurePreviewModal() {
    let dialog = document.getElementById('gldn-preview-modal');
    if (dialog) return dialog;

    dialog = document.createElement('dialog');
    dialog.id = 'gldn-preview-modal';
    dialog.className = 'gldn-preview-modal';
    document.body.appendChild(dialog);
    return dialog;
  }

  function closePreviewModal() {
    const dialog = document.getElementById('gldn-preview-modal');
    if (dialog && dialog.open) {
      try { dialog.close(); } catch (e) {}
    }
  }

  function openPreview(src, title) {
    const dialog = ensurePreviewModal();

    // Content is rebuilt each open — src/title vary per line item.
    dialog.innerHTML =
      '<div class="gldn-preview-modal__content">' +
        '<button type="button" class="gldn-preview-modal__close" aria-label="Close preview">&times;</button>' +
        (title ? '<h3 class="gldn-preview-modal__title">' + title + '</h3>' : '') +
        '<img class="gldn-preview-modal__img" alt="Preview" src="' + src + '">' +
      '</div>';

    // Close on the × button or a tap on the backdrop. The dialog fills the
    // viewport and flex-centers the content card, so a click outside the
    // card lands on the dialog element itself. Esc-to-close is native.
    dialog.onclick = function (e) {
      if (e.target === dialog || e.target.closest('.gldn-preview-modal__close')) {
        closePreviewModal();
      }
    };

    // showModal() throws InvalidStateError on an already-open dialog; close
    // a leaked-open instance first (e.g. opened from a prior line item).
    if (dialog.open) {
      try { dialog.close(); } catch (e) {}
    }

    if (typeof dialog.showModal === 'function') {
      try {
        dialog.showModal();
      } catch (e) {
        dialog.setAttribute('open', ''); // degrade to non-modal
      }
    } else {
      dialog.setAttribute('open', ''); // legacy browser fallback
    }
  }

  // ── Click delegate ────────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    const duplicateBtn = e.target.closest('.cart-items__action-duplicate');
    if (duplicateBtn) {
      e.preventDefault();
      // The free ring sizer is capped at 1 per order (gldn128 / #474).
      // cart-products.liquid never renders Duplicate on its line; this guards
      // stale cart HTML that still carries the button.
      if (duplicateBtn.closest('[data-gldn-ring-sizer="true"]')) return;
      // Prefer the row's live data-key over the button's render-time
      // data-item-key. When a property change (Save Gift Details, etc.)
      // rotates the line-item key, the row's data-key is updated in
      // place but the button's data-item-key is stale until the cart
      // re-renders. Using the stale key here would call
      // findItemByKey(staleKey) → undefined → silent no-op. (APEX 42a2ded5)
      const row = duplicateBtn.closest('[data-key]');
      const key = (row && row.dataset.key) || duplicateBtn.dataset.itemKey;
      handleDuplicateClick(key, duplicateBtn.dataset.itemBuilder);
      return;
    }
    /* gldn167 (#554): NO remove branch here.
     *
     * cart-products.liquid renders the builder Remove button with BOTH this
     * class AND Horizon's `on:click="/onLineItemRemove/<n>"`, so a single tap
     * used to fire two independent removal requests — one addressed by line
     * INDEX (Horizon) and one by line KEY (this file) — each of which
     * gldn-cart-orphan-cleanup then rewrote into its own `/cart.js` read plus a
     * positional `/cart/update.js`. Four requests per tap, and because both
     * positional arrays were computed from a pre-removal snapshot, whichever
     * landed second was indexed against a cart that had already shrunk.
     *
     * Horizon's handler wins: it animates the row out, optimistically swaps the
     * empty-cart state, and is covered by cart-items-component's in-flight
     * disable guard. The builder add-on cascade (remove a parent, remove its
     * `_tag` children) is NOT lost — gldn-cart-orphan-cleanup intercepts any
     * `/cart/change` with quantity 0 and handles the line-index form too.
     *
     * `removeItem()` went with it — it had no other caller. */
    const previewBtn = e.target.closest('.cart-items__preview-piece');
    if (previewBtn) {
      e.preventDefault();
      openPreview(previewBtn.dataset.preview, previewBtn.dataset.title);
      return;
    }
  });
})();
